import React, { useMemo } from 'react';
import { View, Text } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { getWcColors } from './matchPickStats.helper';
import { getPickStatsScoreGridStyles, GRID_SIZE } from './PickStatsScoreGrid.styles';
import type { TeamKitConfig } from '../../../constants/teamKitColors';
import { PickStatsTeamCrest } from './PickStatsTeamCrest';
import type { PickStatsScoreEntry } from '../../../src/worldcup/helpers/matchPickStatsApi';
import { heatMapCellStyle } from './MatchPickStatsScreen.dynamicStyles';

interface PickStatsScoreGridProps {
  scores: PickStatsScoreEntry[];
  homeCountryCode: string | null;
  awayCountryCode: string | null;
  /** Kit mode (AC-10.6): non-null for a club competition, null for a national-team one. */
  homeKit?: TeamKitConfig | null;
  awayKit?: TeamKitConfig | null;
}

const homeGoals = Array.from({ length: GRID_SIZE }, (_, i) => i);
const awayGoals = Array.from({ length: GRID_SIZE }, (_, i) => i);

export const PickStatsScoreGrid: React.FC<PickStatsScoreGridProps> = ({
  scores,
  homeCountryCode,
  awayCountryCode,
  homeKit = null,
  awayKit = null,
}) => {
  const theme = useTheme();
  const C = useMemo(() => getWcColors(theme), [theme]);
  const styles = useMemo(() => getPickStatsScoreGridStyles(C), [C]);

  const pctByScore = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of scores) {
      map.set(`${s.home}:${s.away}`, s.pct);
    }
    return map;
  }, [scores]);

  const maxPct = useMemo(
    () => Math.max(...scores.map((s) => s.pct), 0.001),
    [scores],
  );

  const hotPick = useMemo(() => {
    if (scores.length === 0) return null;
    return scores.reduce((best, s) => (s.pct > best.pct ? s : best), scores[0]);
  }, [scores]);

  return (
    <View style={styles.wrap}>
      {hotPick ? (
        <View style={styles.internalHeader}>
          <View>
            <Text style={styles.internalTitle}>Score predictions</Text>
            <Text style={styles.hotPickLabel}>
              Hot pick:{' '}
              <Text style={styles.hotPickValue}>
                {hotPick.home}–{hotPick.away}
              </Text>
            </Text>
          </View>
        </View>
      ) : null}

      <View style={styles.mainRow}>
        <View style={styles.yFlagCol}>
          <PickStatsTeamCrest countryCode={homeCountryCode} kit={homeKit} size="mini" />
        </View>

        <View>
          <View style={styles.grid}>
            {homeGoals.map((h) => (
              <View key={h} style={styles.row}>
                <Text style={styles.axisLabel}>{h}</Text>
                {awayGoals.map((a) => {
                  const pct = pctByScore.get(`${h}:${a}`) ?? 0;
                  const intensity = pct / maxPct;
                  const isHot = hotPick != null && h === hotPick.home && a === hotPick.away;
                  return (
                    <View key={a} style={[styles.cell, heatMapCellStyle(C.purple, intensity), isHot && styles.cellHot]}>
                      <Text style={styles.cellText}>{Math.round(pct * 100)}%</Text>
                    </View>
                  );
                })}
              </View>
            ))}
          </View>

          <View style={styles.xAxisRow}>
            <View style={styles.xAxisSpacer} />
            {awayGoals.map((a) => (
              <Text key={a} style={styles.xAxisLabel}>{a}</Text>
            ))}
          </View>

          {awayCountryCode || awayKit ? (
            <View style={styles.xFlagRow}>
              <View style={styles.xAxisSpacer} />
              <View style={styles.xFlagCenter}>
                <PickStatsTeamCrest countryCode={awayCountryCode} kit={awayKit} size="mini" />
              </View>
            </View>
          ) : null}
        </View>
      </View>

      <View style={styles.legend}>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, styles.legendDotHot]} />
          <Text style={styles.legendText}>Crowd hot pick</Text>
        </View>
      </View>
    </View>
  );
};
